import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";

const values = [
  { icon: "ri-leaf-line", title: "真实", desc: "只用看得懂的原料，让风味来自食材本身。" },
  { icon: "ri-heart-2-line", title: "用心", desc: "把每一颗糖都当成作品，认真做到位。" },
  { icon: "ri-emotion-happy-line", title: "温暖", desc: "希望每一份甜，都能带来一点好心情。" },
  { icon: "ri-lightbulb-line", title: "持续创新", desc: "不断尝试新品与工艺，让甜蜜有更多可能。" },
];

const milestones = [
  { year: "2014", title: "品牌诞生", desc: "糖心VLGO 在第一间小厨房里诞生，第一颗手工软糖出炉。" },
  { year: "2017", title: "走进更多城市", desc: "产品进入全国 20 多个城市的合作门店。" },
  { year: "2020", title: "少糖配方升级", desc: "全线产品启动减糖计划，推出更清爽的配方。" },
  { year: "2023", title: "线上官方旗舰店", desc: "官方线上渠道上线，品牌与用户的距离更近一步。" },
  { year: "2025", title: "联名与快闪", desc: "与插画师联名、开设线下快闪体验店，探索更多表达。" },
  { year: "2026", title: "荣获年度新锐品牌", desc: "获得「年度新锐甜品品牌」认可，开启新一年甜蜜计划。" },
];

export default function About() {
  return (
    <SiteLayout>
      <PageHero
        title="品牌介绍"
        breadcrumb="品牌介绍"
        subtitle="从第一颗手工软糖开始，糖心VLGO 用十二年时间，把对「真实」的坚持做成了品牌的底色。"
        image="https://readdy.ai/api/search-image?query=Warm%20coral%20and%20cream%20abstract%20artistic%20background%20with%20soft%20flowing%20shapes%20suggesting%20candy%20and%20dessert%2C%20elegant%20minimal%20brand%20banner%2C%20soft%20lighting%2C%20high%20detail&width=1920&height=700&seq=tx-about-hero&orientation=landscape"
      />

      {/* 品牌故事 */}
      <section className="w-full px-4 md:px-10 py-16 md:py-20 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative w-full h-[320px] md:h-[460px] rounded-lg overflow-hidden bg-background-100">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20artistic%20photo%20of%20hands%20making%20handmade%20candy%20in%20a%20cozy%20kitchen%2C%20soft%20natural%20light%2C%20coral%20accents%2C%20elegant%20editorial%20food%20photography%2C%20high%20detail%2C%20authentic%20craft%20atmosphere&width=1000&height=1250&seq=tx-about-story&orientation=portrait"
              alt="糖心VLGO 品牌故事：手工制作场景"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600">
              <span className="w-6 h-[2px] bg-accent-500"></span>
              品牌故事
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 tracking-tight">
              始于一颗
              <br />
              手工软糖
            </h2>
            <div className="mt-5 flex flex-col gap-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                2014 年，糖心VLGO 的第一颗软糖在一间不到十平方米的小厨房里诞生。
                创始人希望做出「自己也愿意天天吃的糖」——不加多余的东西，只用真实的水果和原料。
              </p>
              <p>
                从那时起，「真实」就成了品牌最重要的原则。我们坚持小批量慢工制作，
                反复调整配方与口感，让每一款产品都经得起反复品尝。
              </p>
              <p>
                十二年过去，糖心VLGO 从一个手作小品牌，成长为被越来越多用户喜爱的甜品品牌。
                不变的是，那颗最初被认真对待的软糖，依然是我们的起点与标准。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 文化与使命 */}
      <section className="w-full bg-secondary-50">
        <div className="w-full px-4 md:px-10 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-accent-600">
              <span className="w-6 h-[2px] bg-accent-500"></span>
              文化与使命
              <span className="w-6 h-[2px] bg-accent-500"></span>
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 tracking-tight">
              让每一份甜，来得更自然
            </h2>
            <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
              糖心VLGO 的使命很简单：用真实的原料与用心的工艺，
              做出让人吃得安心、吃得开心的甜点，把小小的幸福传递出去。
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="flex flex-col items-center text-center rounded-lg bg-background-50 border border-background-200 p-8"
              >
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className={`${v.icon} text-2xl leading-none`}></i>
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 发展里程碑 */}
      <section className="w-full px-4 md:px-10 py-16 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-accent-600">
            <span className="w-6 h-[2px] bg-accent-500"></span>
            发展里程碑
            <span className="w-6 h-[2px] bg-accent-500"></span>
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 tracking-tight">
            一路走来的甜蜜印记
          </h2>
        </div>

        <div className="mt-14 max-w-3xl mx-auto">
          <div className="relative border-l-2 border-background-200 pl-8 md:pl-12 flex flex-col gap-10">
            {milestones.map((m) => (
              <div key={m.year} className="relative">
                <span className="absolute -left-[41px] md:-left-[57px] top-1 w-5 h-5 rounded-full bg-primary-500 ring-4 ring-background-50"></span>
                <span className="font-heading text-2xl font-extrabold text-primary-600">{m.year}</span>
                <h3 className="mt-1 font-heading text-lg font-bold text-foreground-950">{m.title}</h3>
                <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line leading-none"></i>
            返回首页
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}